import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";

import {
  LOT_PAR_ETAT_DATA,
  LotParEtatDluoItem,
  LotParEtatNcItem,
} from "../../models/data.model";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { SharedModule } from "../../../../shared/shared.module";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DateLimiteDisplayComponent } from "../../../../shared/components/date-limite-display/date-limite-display.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { ArticleSmallCardComponent } from "../../../../shared/components/article-small-card/article-small-card.component";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

import {
  LotParEtatDialogComponent,
  LotParEtatDialogData,
} from "./lot-par-etat-dialog.component";

type LotType = "ddm" | "dlc" | "dlcImminente" | "nc";

interface PreviewLine {
  lotId: string;
  articleId: string;
  articleDenomination: string;
  numeroLot: string;
  emplacement: string;
  quantite: number;
  coutTotal: number;
  dluo?: string;
  dluoFormat?: string;
  typeDateLimite?: "DDM" | "DLC" | "AUCUNE";
}

const TOP_N = 10;

/**
 * Widget "Lots par état" — version refondue alignée sur le pattern
 * `valeur-par-famille` / `ca-par-periode` :
 *  - Toggle simple DLUO / NC (les redirects NC ouvertes/critiques sont
 *    déplacés vers le widget NC dédié, hors scope ici).
 *  - Top 10 lignes les plus coûteuses (tri par coût total décr.).
 *  - Bouton "Voir tout (N)" qui ouvre `LotParEtatDialogComponent` pour
 *    consulter l'intégralité avec pivot famille/article/emplacement,
 *    stats cards et export Excel multi-feuilles.
 */
@Component({
  selector: "vl-widget-lot-par-etat",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    MatIcon,
    MatTooltip,
    WidgetTitleComponent,
    ChipsComponent,
    DateLimiteDisplayComponent,
    LotCardComponent,
    ArticleSmallCardComponent,
    AutoSignificantDigitsPipe,
  ],
  templateUrl: "./widget-lot-par-etat.component.html",
  styleUrl: "./widget-lot-par-etat.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetLotParEtatComponent {
  data = input<LOT_PAR_ETAT_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  selected = signal<LotType>("ddm");

  private dialogs = inject(DialogsService);

  /** Items DDM (date durabilité minimale) dépassés. */
  ddmItems = computed<LotParEtatDluoItem[]>(() =>
    (this.data()?.dluo ?? []).filter(
      (it) => it.typeDateLimite !== "DLC",
    ),
  );

  /** Items DLC (date limite consommation) dépassés — strict, blocage légal. */
  dlcItems = computed<LotParEtatDluoItem[]>(() =>
    (this.data()?.dluo ?? []).filter((it) => it.typeDateLimite === "DLC"),
  );

  /** Items DLC imminents (J→J+7) — encore valides mais à surveiller. */
  dlcImminenteItems = computed<LotParEtatDluoItem[]>(
    () => this.data()?.dlcImminente ?? [],
  );

  /** Items du type sélectionné. */
  private items = computed<(LotParEtatDluoItem | LotParEtatNcItem)[]>(() => {
    switch (this.selected()) {
      case "ddm":
        return this.ddmItems();
      case "dlc":
        return this.dlcItems();
      case "dlcImminente":
        return this.dlcImminenteItems();
      case "nc":
        return this.data()?.nc ?? [];
    }
  });

  /** Nombre total de lots du type sélectionné. */
  totalLots = computed(() => this.items().length);

  /** Valeur cumulée immobilisée (€). */
  totalCout = computed(() =>
    this.items().reduce((s, it) => s + (it.coutTotal ?? 0), 0),
  );

  /**
   * Aperçu top 10 par coût total. 1 ligne par (lot × stock) — un même lot
   * éclaté sur plusieurs adresses apparaît sur plusieurs lignes mais reste
   * compté une seule fois dans `totalLots`.
   */
  preview = computed<PreviewLine[]>(() => {
    const lines: PreviewLine[] = [];
    const sel = this.selected();
    const isDluo = sel === "ddm" || sel === "dlc" || sel === "dlcImminente";
    for (const it of this.items()) {
      for (const st of it.stocks ?? []) {
        const coutUnitaire = st.coutUnitaire ?? it.coutUnitaire ?? 0;
        lines.push({
          lotId: it.lotId,
          articleId: it.articleId,
          articleDenomination: it.articleDenomination ?? "—",
          numeroLot: it.numeroLot ?? "—",
          emplacement: st.adresseStockDenomination ?? "—",
          quantite: st.quantite ?? 0,
          coutTotal: (st.quantite ?? 0) * coutUnitaire,
          dluo: isDluo ? (it as LotParEtatDluoItem).dluoString : undefined,
          dluoFormat: isDluo
            ? (it as LotParEtatDluoItem).dluoFormat
            : undefined,
          typeDateLimite: isDluo
            ? (it as LotParEtatDluoItem).typeDateLimite
            : undefined,
        });
      }
    }
    lines.sort((a, b) => b.coutTotal - a.coutTotal);
    return lines.slice(0, TOP_N);
  });

  /** Nombre total de lignes (stocks éclatés) — pour libellé "voir tout (N)". */
  totalLignes = computed(() => {
    let n = 0;
    for (const it of this.items()) n += (it.stocks ?? []).length;
    return n;
  });

  /** Ouvre le dialog "Voir tout" avec data + pivots + export. */
  openFullList(): void {
    const d = this.data();
    if (!d) return;
    // ⚠️ NE PAS passer de hauteur fixe (ex: '92vh') : Mat-dialog-surface
    // crée son propre scroll en plus de `.justup-dialog-content`, donnant
    // un double scrollbar. `fit-content` laisse le `.justup-dialog` interne
    // se capper à 90dvh et gérer le scroll seul (cf. memory feedback-
    // no-double-scroll).
    this.dialogs.open(
      LotParEtatDialogComponent,
      "min(1200px, 95vw)",
      "fit-content",
      { data: d } satisfies LotParEtatDialogData,
    );
  }
}
